import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout/Layout";
import HomePage from "./pages/HomePage";
import BrandPage from "./pages/BrandPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import SectorPage from "./pages/SectorPage";
import LightingOverviewPage from "./pages/LightingOverviewPage";
import CatalogueCategoryPage from "./pages/CatalogueCategoryPage";
import FamiliesPage from "./pages/FamiliesPage";
import FamilyPage from "./pages/FamilyPage";
import NewsPage from "./pages/NewsPage";
import NewsArticlePage from "./pages/NewsArticlePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";

// Admin pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminLandingPage from "./pages/admin/AdminLandingPage";
import AdminCompany from "./pages/admin/AdminCompany";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminBrandEdit from "./pages/admin/AdminBrandEdit";
import AdminNews from "./pages/admin/AdminNews";
import AdminAbout from "./pages/admin/AdminAbout";
import AdminSectors from "./pages/admin/AdminSectors";
import AdminSectorEdit from "./pages/admin/AdminSectorEdit";
import AdminMediaLibrary from "./pages/admin/AdminMediaLibrary";
import AdminContactSubmissions from "./pages/admin/AdminContactSubmissions";
import AdminChangePassword from "./pages/admin/AdminChangePassword";

import { authApi, clearAdminToken } from "./services/api";
import usePageTracking from "./hooks/usePageTracking";
import { Toaster } from "./components/ui/sonner";

// Scroll to top and track page views on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  usePageTracking();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, isAuthenticated, isLoading }) => {
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authApi.verifyToken();
        setIsAuthenticated(response.data.valid);
      } catch (err) {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount — checks cookie-based auth

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    clearAdminToken();
    setIsAuthenticated(false);
  };

  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public routes */}
          <Route element={<Layout><HomePage /></Layout>} path="/" />
          <Route element={<Layout><BrandPage /></Layout>} path="/brand/:brandId" />
          <Route element={<Layout><ProductDetailPage /></Layout>} path="/brand/:brandId/products/:slug" />
          <Route element={<Layout><LightingOverviewPage /></Layout>} path="/brand/:brandId/lighting" />
          <Route element={<Layout><CatalogueCategoryPage /></Layout>} path="/brand/:brandId/lighting/:category" />
          <Route element={<Layout><CatalogueCategoryPage /></Layout>} path="/brand/:brandId/search" />
          <Route element={<Layout><FamiliesPage /></Layout>} path="/brand/:brandId/families" />
          <Route element={<Layout><FamilyPage /></Layout>} path="/brand/:brandId/families/:familySlug" />
          <Route element={<Layout><SectorPage /></Layout>} path="/sector/:sectorId" />
          <Route element={<Layout><NewsPage /></Layout>} path="/news" />
          <Route element={<Layout><NewsArticlePage /></Layout>} path="/news/:newsId" />
          <Route element={<Layout><AboutPage /></Layout>} path="/about" />
          <Route element={<Layout><ContactPage /></Layout>} path="/contact" />

          {/* Admin Login */}
          <Route 
            path="/admin/login" 
            element={
              isAuthenticated ? (
                <Navigate to="/admin" replace />
              ) : (
                <AdminLogin onLogin={handleLogin} />
              )
            } 
          />

          {/* Protected Admin routes */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <AdminLayout onLogout={handleLogout} />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="landing-page" element={<AdminLandingPage />} />
            <Route path="company" element={<AdminCompany />} />
            <Route path="brands" element={<AdminBrands />} />
            <Route path="brands/:brandId" element={<AdminBrandEdit />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="sectors" element={<AdminSectors />} />
            <Route path="sectors/:sectorId" element={<AdminSectorEdit />} />
            <Route path="about" element={<AdminAbout />} />
            <Route path="media" element={<AdminMediaLibrary />} />
            <Route path="contacts" element={<AdminContactSubmissions />} />
            <Route path="change-password" element={<AdminChangePassword onLogout={handleLogout} />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
